import React, { useEffect, useState } from "react";
import styles from "./bulk-upload.module.css";
import CustomDrawer from "../../common/custom-drawer/custom-drawer";
import CustomUpload from "../../common/CustomUpload/CustomUpload";
import CustomButton from "../../common/custom-button/custom-button";
import CustomSelect from "../../common/input/custom-select/custom-select";
import { Col, Row, Select, Typography, Upload } from "antd";
import { Controller, useForm } from "react-hook-form";
import { checkMixedSchool, createAntdSelection, downloadS3File, getSampleFileConfig } from "../../utils/common";
import { fetchClass, fetchSections } from "../../services/student";
import { DownloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useAcademicYears } from "../../hooks/useAcademicYear";

const BulkUpload = ({
  label = "Bulk Upload",
  onClose,
  open,
  errors,
  handleClickUpload,
  accepts,
  isStudentPage = false,
}) => {
  const [levels, setLevels] = useState([]);
  const [classRooms, setClassRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLevelId, setSelectedLevelId] = useState("");
  const user = useSelector((state) => state.user?.user);
  const isMixedSchool = checkMixedSchool(user);
  const { sampleFileName, sampleFileLink } = getSampleFileConfig({
    isStudentPage,
    isMixedSchool,
  });
  const { control, trigger, getValues, reset, setValue } = useForm({
    mode: "onChange",
    defaultValues: {
      file: null,
      classroom: "",
      level: "",
      academic_year_id: "",
    },
  });
  const {
    yearList,
    selectedYear: activeAcademicYear,
    onPopupScroll,
  } = useAcademicYears();

  const handleUploadFile = async () => {
    try {
      const isValid = await trigger();
      if (isValid) {
        const { classroom, file, academic_year_id } = getValues();
        setLoading(true);
        await handleClickUpload(file, classroom, academic_year_id);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      reset({ level: "", classroom: "", file: "" });
    }
  }, [open]);

  useEffect(() => {
    (async () => {
      try {
        if (!open || !isStudentPage) return;
        const resp = await fetchClass();
        const data = resp?.data?.data?.levels;
        const buildObj = createAntdSelection(data);
        setLevels(buildObj);
      } catch (err) {
        console.log(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      try {
        if (!open || !isStudentPage || !selectedLevelId) return;
        const resp = await fetchSections(selectedLevelId);
        const data = resp?.data?.data?.classrooms;
        const buildObj = createAntdSelection(data);
        setClassRooms(buildObj);
      } catch (err) {
        console.log(err);
      }
    })();
  }, [open, selectedLevelId]);

  useEffect(() => {
    if (activeAcademicYear) {
      setValue("academic_year_id", activeAcademicYear);
    }
  }, [activeAcademicYear]);

  return (
    <>
      <CustomDrawer
        title={label}
        open={open}
        onClose={() => {
          onClose();
        }}
        width="30vw"
        loading={loading}
        className={styles.drawer}
        drawerStyles={{ body: { padding: 0, margin: 0 } }}
      >
        <div className={styles.layoutMain}>
          <div className={styles.mainLayout}>
            <Row gutter={[16, 16]}>
              {isStudentPage ? (
                <>
                  <Col span={24}>
                    <label
                      style={{
                        fontWeight: 500,
                        marginBottom: 6,
                        display: "inline-block",
                      }}
                    >
                      Select Academic Year
                    </label>
                    <Controller
                      name="academic_year_id"
                      control={control}
                      rules={{ required: true }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <CustomSelect
                          labelRowor="#171717"
                          placeholder="--Select--"
                          size="large"
                          value={value}
                          onChange={onChange}
                          onPopupScroll={onPopupScroll}
                          status={error ? "error" : ""}
                        >
                          {yearList.map((item, index) => (
                            <Select.Option key={item?.id || index} value={item?.id}>
                              {item?.name}
                            </Select.Option>
                          ))}
                        </CustomSelect>
                      )}
                    />
                  </Col>
                  <Col span={24}>
                    <label
                      style={{
                        fontWeight: 500,
                        marginBottom: 6,
                        display: "inline-block",
                      }}
                    >
                      Select Level
                    </label>
                    <Controller
                      name="level"
                      control={control}
                      rules={{ required: true }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <CustomSelect
                          labelRowor="#171717"
                          placeholder="--Select--"
                          showSearch
                          size="large"
                          value={value}
                          onChange={(val) => {
                            onChange(val);
                            setSelectedLevelId(val);
                            setValue("classroom", "");
                          }}
                          status={error ? "error" : ""}
                          filterOption={(input, option) =>
                            option?.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {levels?.map((option) => (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </Select.Option>
                          ))}
                        </CustomSelect>
                      )}
                    />
                  </Col>
                  <Col span={24}>
                    <label
                      style={{
                        fontWeight: 500,
                        marginBottom: 6,
                        display: "inline-block",
                      }}
                    >
                      Select Classroom
                    </label>
                    <Controller
                      name="classroom"
                      control={control}
                      rules={{ required: true }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <CustomSelect
                          labelRowor="#171717"
                          placeholder="--Select--"
                          showSearch
                          size="large"
                          value={value}
                          onChange={onChange}
                          status={error ? "error" : ""}
                          filterOption={(input, option) =>
                            option?.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {classRooms?.map((option) => (
                            <Select.Option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </Select.Option>
                          ))}
                        </CustomSelect>
                      )}
                    />
                  </Col>
                </>
              ) : null}
              <Col span={24}>
                <Controller
                  name="file"
                  control={control}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomUpload
                      accepts={accepts}
                      showFileFormats={false}
                      handleBeforeUpload={(file) => {
                        const allowedExtensions = accepts.split(',');
                        const isValid = allowedExtensions.some((ext) =>
                          file.name.toLowerCase().endsWith(ext)
                        );
                        if (!isValid) {
                          toast.error("Only XLSX files are allowed!");
                          return Upload.LIST_IGNORE;
                        }
                        onChange(file);
                        return false;
                      }}
                      fileName={value?.name}
                      label="Upload XLSX File"
                    />
                  )}
                />
              </Col>
            </Row>

            <div className={styles.sampleText}>
              <Typography className={styles.sampleLabel}>
                {sampleFileName}
              </Typography>
              <span 
                className={styles.downloadIcon}
                onClick={() => downloadS3File(sampleFileLink, sampleFileName)}
              >
                <DownloadOutlined />
              </span>
            </div>

            <CustomButton
              onClick={handleUploadFile}
              title="Upload"
              type="submit"
              style={{
                width: "128px",
                height: "40px",
                margin: "0 0 0 auto",
              }}
              variant="outlined"
            />

            <Row>
              {errors?.map((i, idx) => {
                return (
                  <p style={{ color: "red" }} key={idx}>
                    {i}
                  </p>
                );
              })}
            </Row>
          </div>
        </div>
      </CustomDrawer>
    </>
  );
};
export default BulkUpload;
